import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { LoginResponse, StaffInfo } from '../../types/entity/auth';

const AUTH_STORAGE_KEY = 'auth.profile';

interface AuthState {
  token: string | null;
  expiresAt: string | null;
  staff: StaffInfo | null;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  token: string | null;
  staff: StaffInfo | null;
  roles: string[];
  permissions: string[];
  login: (payload: LoginResponse) => void;
  logout: () => void;
  can: (permission: string | string[]) => boolean;
  hasRole: (role: string | string[]) => boolean;
  syncFromStorage: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const parseStoredAuth = (): AuthState => {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) {
      return { token: null, expiresAt: null, staff: null };
    }
    const parsed = JSON.parse(raw);
    return {
      token: parsed.token ?? null,
      expiresAt: parsed.expires_at ?? null,
      staff: parsed.staff ?? null,
    };
  } catch (error) {
    console.warn('Failed to parse auth profile from storage', error);
    return { token: null, expiresAt: null, staff: null };
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>(() => parseStoredAuth());

  const storeState = useCallback((next: AuthState) => {
    setState(next);
    if (!next.token || !next.staff) {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      localStorage.removeItem('token');
      localStorage.removeItem('username');
      localStorage.removeItem('staff');
      localStorage.removeItem('isLoggedIn');
      return;
    }

    const payload = {
      token: next.token,
      expires_at: next.expiresAt,
      staff: next.staff,
    };
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(payload));
    localStorage.setItem('token', next.token);
    localStorage.setItem('username', next.staff.username);
    localStorage.setItem('staff', JSON.stringify(next.staff));
  }, []);

  const login = useCallback((payload: LoginResponse) => {
    storeState({
      token: payload.token,
      expiresAt: payload.expires_at,
      staff: payload.staff,
    });
  }, [storeState]);

  const logout = useCallback(() => {
    storeState({ token: null, expiresAt: null, staff: null });
  }, [storeState]);

  const syncFromStorage = useCallback(() => {
    storeState(parseStoredAuth());
  }, [storeState]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === AUTH_STORAGE_KEY) {
        setState(parseStoredAuth());
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const permissions = useMemo(() => state.staff?.permissions ?? [], [state.staff]);
  const roles = useMemo(() => state.staff?.roles ?? [], [state.staff]);
  const normalizedRoles = useMemo(() => roles.map((role) => role.toLowerCase()), [roles]);
  const isAdminLike = useMemo(
    () => normalizedRoles.some((role) => role === 'admin' || role === 'super_admin'),
    [normalizedRoles]
  );

  const can = useCallback((permission: string | string[]) => {
    if (!permission) {
      return true;
    }
    if (isAdminLike) {
      return true;
    }
    const targets = Array.isArray(permission) ? permission : [permission];
    if (targets.length === 0) {
      return true;
    }
    
    // Check if user has permission (supporting wildcards)
    // Rule:
    // 1. Exact match: owned "A" matches required "A"
    // 2. Wildcard match: owned "A.*" matches required "A.B"
    // 3. Global match: owned "*" matches everything
    const hasPermission = (ownedPerm: string, requiredPerm: string) => {
      if (ownedPerm === '*' || ownedPerm === requiredPerm) {
        return true;
      }
      if (ownedPerm.endsWith('.*')) {
        const prefix = ownedPerm.slice(0, -2);
        return requiredPerm.startsWith(prefix + '.');
      }
      return false;
    };

    return targets.every((target) => 
      permissions.some(owned => hasPermission(owned, target))
    );
  }, [permissions, isAdminLike]);

  const hasRole = useCallback((role: string | string[]) => {
    if (!role) {
      return true;
    }
    const targets = Array.isArray(role) ? role : [role];
    if (targets.length === 0) {
      return true;
    }
    const owned = new Set(normalizedRoles);
    return targets.every((item) => owned.has(item.toLowerCase()));
  }, [normalizedRoles]);

  const value = useMemo<AuthContextValue>(() => ({
    isAuthenticated: Boolean(state.token && state.staff),
    token: state.token,
    staff: state.staff,
    roles,
    permissions,
    login,
    logout,
    can,
    hasRole,
    syncFromStorage,
  }), [state.token, state.staff, roles, permissions, login, logout, can, hasRole, syncFromStorage]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};
